import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { CollapseContent } from '../../../../../../../components/Collapse/CollapseContent'
import { RouteColumn, RouteRow } from '../../../../../../../components/layout'
import { SelectInput } from '../../../../../../../components/layout/form/input'
import { FinishedDateTimeInput } from '../../../../../../../components/layout/form/input/DateTimeInput'
import { format } from 'date-fns'
import { Time } from '../../../../../../../types/time'
import { TowingRecordJobStatus } from '../../../../../../../common/types'
import { serviceTypeOptions } from '../../../../../../TowingOrderPage/options'
import { ServiceType } from '../../../../../../../functions/graphqlApi/types/jobDetails'
import { Modal } from '../../../../../../../components/Modal'
import { DefaultProps, FinishedAtBreakdownLocationProps } from './FinishedForm'
import { useSettings } from '../../../../../../Parameters'
import { routeSubformMessages } from '../../messages'
import { translateInputOptions } from '../../../../../../../util/translateInputOptions'

export const FinishedAtBreakdownLocationForm: React.FunctionComponent<
  FinishedAtBreakdownLocationProps & DefaultProps
> = ({ setValue, getValue, action, jobStatus, autoliittoFields }) => {
  const intl = useIntl()
  const [showModal, setShowModal] = useState(false)
  const [warned, setWarned] = useState(() => {
    if (autoliittoFields.hasFinishRouteInfo) return false
    return true
  })

  const [edit, setEdit] = useState<boolean>(
    action === 'edit-finished' || jobStatus === TowingRecordJobStatus.arrived_at_breakdown_location
  )
  const settings = useSettings()

  const handleOnServiceTypeChange = (serviceType: string | number | undefined): void => {
    const alert = !warned && (serviceType !== 'towing' && serviceType !== 'other')
    if (alert) {
      setWarned(true)
      setShowModal(true)
    }
    setValue('serviceType', serviceType)
  }

  const options = (settings && settings.towingReasons) || []
  const translatedServiceTypeOptions = translateInputOptions(serviceTypeOptions, intl.formatMessage)
  const towingReasonIsRequired = ![ServiceType.TOWING, ServiceType.OTHER].includes(getValue('serviceType'))

  useEffect(() => {
    if (action === 'edit-finished') {
      if (!edit) setEdit(true)
    } else {
      if (edit) setEdit(false)
    }
  }, [edit, action, setEdit])

  return (
    <CollapseContent>
      {showModal && (
        <Modal
          label={intl.formatMessage(routeSubformMessages.attention)}
          closeLabel={intl.formatMessage(routeSubformMessages.cancelModal)}
          message={intl.formatMessage(routeSubformMessages.roadServiceWarningMessage)}
          disableCloseOnConfirm
          onConfirm={() => {
            setShowModal(false)
          }}
          close={() => {
            setShowModal(false)
            setValue('serviceType', 'towing')
          }}
        />
      )}
      <RouteRow columns={`1fr 1fr 1fr`}>
        <RouteColumn>
          <SelectInput
            label={intl.formatMessage(routeSubformMessages.action)}
            name="serviceType"
            options={translatedServiceTypeOptions}
            onChange={handleOnServiceTypeChange}
            disabled={!edit}
            required
          />
        </RouteColumn>
        <RouteColumn>
          <SelectInput
            label={intl.formatMessage(routeSubformMessages.reason)}
            name="towingReason"
            options={options.map((label: string) => ({
              label,
              value: label,
            }))}
            required={towingReasonIsRequired}
            disabled={!edit && autoliittoFields.disableTowingReason}
            dataTestId="towing-reason-select"
          />
        </RouteColumn>
        <RouteColumn>
          <FinishedDateTimeInput
            name="arrivalRoute"
            label={intl.formatMessage(routeSubformMessages.completionTime)}
            customOnChange={() => {
              const value = (format(new Date(), 'HH:mm') as unknown) as Time
              setValue('arrivalRoute.finishedTime', value)
              setValue('arrivalRoute.finishedDate', new Date())
            }}
            required={edit}
            disabled={!edit}
            showCurrentTimeBtn
          />
        </RouteColumn>
      </RouteRow>
    </CollapseContent>
  )
}

export const finishedAtDestinationEditableJobStatuses = [TowingRecordJobStatus.finished_at_destination]
